/**
 * proef's grape lexicon: a stable code per variety, so a card can say "Nebbiolo" without carrying
 * a typed string. Names are the varieties' own proper nouns (not translated); aliases cover the
 * synonyms and local spellings people type. Our own list, not copied from a reference work.
 */
import { foldName } from "./fold.js";

export interface Grape {
  /** Kebab-case, ASCII, stable across revisions. */
  readonly code: string;
  readonly name: string;
  readonly colour: "white" | "red";
  readonly aliases?: readonly string[];
}

export const GRAPES: readonly Grape[] = [
  // ---- red ----
  { code: "cabernet-sauvignon", name: "Cabernet Sauvignon", colour: "red", aliases: ["Cab Sauv"] },
  { code: "merlot", name: "Merlot", colour: "red" },
  {
    code: "pinot-noir",
    name: "Pinot Noir",
    colour: "red",
    aliases: ["Spätburgunder", "Blauburgunder", "Pinot Nero"],
  },
  { code: "syrah", name: "Syrah", colour: "red", aliases: ["Shiraz"] },
  {
    code: "grenache",
    name: "Grenache",
    colour: "red",
    aliases: ["Garnacha", "Garnacha Tinta", "Cannonau"],
  },
  {
    code: "tempranillo",
    name: "Tempranillo",
    colour: "red",
    aliases: ["Tinto Fino", "Tinta de Toro", "Tinta Roriz", "Aragonez", "Cencibel"],
  },
  {
    code: "sangiovese",
    name: "Sangiovese",
    colour: "red",
    aliases: ["Brunello", "Prugnolo Gentile"],
  },
  { code: "nebbiolo", name: "Nebbiolo", colour: "red", aliases: ["Spanna", "Chiavennasca"] },
  { code: "barbera", name: "Barbera", colour: "red" },
  { code: "malbec", name: "Malbec", colour: "red", aliases: ["Cot", "Côt", "Auxerrois Rouge"] },
  { code: "cabernet-franc", name: "Cabernet Franc", colour: "red", aliases: ["Cab Franc"] },
  { code: "carmenere", name: "Carménère", colour: "red" },
  { code: "zinfandel", name: "Zinfandel", colour: "red", aliases: ["Primitivo"] },
  { code: "gamay", name: "Gamay", colour: "red", aliases: ["Gamay Noir"] },
  {
    code: "mourvedre",
    name: "Mourvèdre",
    colour: "red",
    aliases: ["Monastrell", "Mataro"],
  },
  { code: "pinotage", name: "Pinotage", colour: "red" },
  { code: "tannat", name: "Tannat", colour: "red" },
  { code: "touriga-nacional", name: "Touriga Nacional", colour: "red" },
  { code: "touriga-franca", name: "Touriga Franca", colour: "red" },
  { code: "montepulciano", name: "Montepulciano", colour: "red" },
  { code: "nero-d-avola", name: "Nero d'Avola", colour: "red", aliases: ["Calabrese"] },
  { code: "aglianico", name: "Aglianico", colour: "red" },
  { code: "dolcetto", name: "Dolcetto", colour: "red" },
  { code: "corvina", name: "Corvina", colour: "red", aliases: ["Corvina Veronese"] },
  { code: "petit-verdot", name: "Petit Verdot", colour: "red" },
  {
    code: "carignan",
    name: "Carignan",
    colour: "red",
    aliases: ["Cariñena", "Mazuelo", "Carignano"],
  },
  { code: "cinsault", name: "Cinsault", colour: "red", aliases: ["Cinsaut"] },
  {
    code: "blaufrankisch",
    name: "Blaufränkisch",
    colour: "red",
    aliases: ["Lemberger", "Kékfrankos", "Frankovka"],
  },
  { code: "zweigelt", name: "Zweigelt", colour: "red", aliases: ["Blauer Zweigelt"] },
  {
    code: "sankt-laurent",
    name: "St. Laurent",
    colour: "red",
    aliases: ["Sankt Laurent", "Saint Laurent"],
  },
  { code: "mencia", name: "Mencía", colour: "red" },
  { code: "bobal", name: "Bobal", colour: "red" },
  { code: "negroamaro", name: "Negroamaro", colour: "red" },
  { code: "nerello-mascalese", name: "Nerello Mascalese", colour: "red" },
  { code: "frappato", name: "Frappato", colour: "red" },
  { code: "lagrein", name: "Lagrein", colour: "red" },
  { code: "teroldego", name: "Teroldego", colour: "red" },
  { code: "xinomavro", name: "Xinomavro", colour: "red" },
  { code: "agiorgitiko", name: "Agiorgitiko", colour: "red", aliases: ["Saint George"] },
  { code: "saperavi", name: "Saperavi", colour: "red" },
  { code: "baga", name: "Baga", colour: "red" },
  { code: "trincadeira", name: "Trincadeira", colour: "red", aliases: ["Tinta Amarela"] },
  { code: "castelao", name: "Castelão", colour: "red", aliases: ["Periquita"] },
  { code: "petite-sirah", name: "Petite Sirah", colour: "red", aliases: ["Durif"] },
  { code: "schiava", name: "Schiava", colour: "red", aliases: ["Vernatsch", "Trollinger"] },
  { code: "lambrusco", name: "Lambrusco", colour: "red" },
  { code: "sagrantino", name: "Sagrantino", colour: "red" },
  { code: "refosco", name: "Refosco", colour: "red", aliases: ["Refosco dal Peduncolo Rosso"] },
  {
    code: "pinot-meunier",
    name: "Pinot Meunier",
    colour: "red",
    aliases: ["Meunier", "Schwarzriesling"],
  },
  { code: "cesanese", name: "Cesanese", colour: "red" },
  { code: "grignolino", name: "Grignolino", colour: "red" },
  { code: "freisa", name: "Freisa", colour: "red" },
  { code: "dornfelder", name: "Dornfelder", colour: "red" },
  { code: "tinta-barroca", name: "Tinta Barroca", colour: "red" },
  // ---- white ----
  { code: "chardonnay", name: "Chardonnay", colour: "white" },
  { code: "sauvignon-blanc", name: "Sauvignon Blanc", colour: "white", aliases: ["Fumé Blanc"] },
  {
    code: "riesling",
    name: "Riesling",
    colour: "white",
    aliases: ["Weisser Riesling", "Rheinriesling"],
  },
  {
    code: "chenin-blanc",
    name: "Chenin Blanc",
    colour: "white",
    aliases: ["Steen", "Pineau de la Loire"],
  },
  {
    code: "pinot-gris",
    name: "Pinot Gris",
    colour: "white",
    aliases: ["Pinot Grigio", "Grauburgunder", "Grauer Burgunder", "Ruländer"],
  },
  {
    code: "pinot-blanc",
    name: "Pinot Blanc",
    colour: "white",
    aliases: ["Weissburgunder", "Weisser Burgunder", "Pinot Bianco"],
  },
  { code: "gewurztraminer", name: "Gewürztraminer", colour: "white", aliases: ["Traminer"] },
  { code: "viognier", name: "Viognier", colour: "white" },
  { code: "semillon", name: "Sémillon", colour: "white" },
  {
    code: "muscat",
    name: "Muscat",
    colour: "white",
    aliases: [
      "Moscato",
      "Muscat Blanc à Petits Grains",
      "Muskateller",
      "Moscatel",
      "Muscat of Alexandria",
    ],
  },
  { code: "albarino", name: "Albariño", colour: "white", aliases: ["Alvarinho"] },
  { code: "verdejo", name: "Verdejo", colour: "white" },
  { code: "gruner-veltliner", name: "Grüner Veltliner", colour: "white" },
  { code: "garganega", name: "Garganega", colour: "white" },
  {
    code: "trebbiano",
    name: "Trebbiano",
    colour: "white",
    aliases: ["Ugni Blanc", "Trebbiano Toscano", "Trebbiano d'Abruzzo"],
  },
  {
    code: "vermentino",
    name: "Vermentino",
    colour: "white",
    aliases: ["Rolle", "Pigato", "Favorita"],
  },
  { code: "fiano", name: "Fiano", colour: "white" },
  { code: "greco", name: "Greco", colour: "white", aliases: ["Greco di Tufo"] },
  { code: "falanghina", name: "Falanghina", colour: "white" },
  { code: "glera", name: "Glera", colour: "white", aliases: ["Prosecco"] },
  { code: "cortese", name: "Cortese", colour: "white" },
  { code: "arneis", name: "Arneis", colour: "white" },
  { code: "marsanne", name: "Marsanne", colour: "white" },
  { code: "roussanne", name: "Roussanne", colour: "white" },
  {
    code: "melon-de-bourgogne",
    name: "Melon de Bourgogne",
    colour: "white",
    aliases: ["Muscadet", "Melon"],
  },
  { code: "muller-thurgau", name: "Müller-Thurgau", colour: "white", aliases: ["Rivaner"] },
  { code: "silvaner", name: "Silvaner", colour: "white", aliases: ["Sylvaner"] },
  { code: "furmint", name: "Furmint", colour: "white" },
  { code: "harslevelu", name: "Hárslevelű", colour: "white" },
  { code: "assyrtiko", name: "Assyrtiko", colour: "white" },
  { code: "godello", name: "Godello", colour: "white" },
  { code: "viura", name: "Viura", colour: "white", aliases: ["Macabeo", "Macabeu"] },
  { code: "xarel-lo", name: "Xarel·lo", colour: "white", aliases: ["Xarello"] },
  { code: "parellada", name: "Parellada", colour: "white" },
  { code: "palomino", name: "Palomino", colour: "white", aliases: ["Palomino Fino"] },
  { code: "pedro-ximenez", name: "Pedro Ximénez", colour: "white", aliases: ["PX"] },
  { code: "loureiro", name: "Loureiro", colour: "white" },
  { code: "arinto", name: "Arinto", colour: "white", aliases: ["Pedernã"] },
  { code: "encruzado", name: "Encruzado", colour: "white" },
  { code: "bical", name: "Bical", colour: "white" },
  { code: "malvasia", name: "Malvasia", colour: "white", aliases: ["Malvazija", "Malmsey"] },
  { code: "verdicchio", name: "Verdicchio", colour: "white" },
  { code: "pecorino", name: "Pecorino", colour: "white" },
  { code: "passerina", name: "Passerina", colour: "white" },
  {
    code: "friulano",
    name: "Friulano",
    colour: "white",
    aliases: ["Tocai Friulano", "Sauvignonasse"],
  },
  { code: "ribolla-gialla", name: "Ribolla Gialla", colour: "white", aliases: ["Rebula"] },
  { code: "torrontes", name: "Torrontés", colour: "white" },
  { code: "colombard", name: "Colombard", colour: "white", aliases: ["French Colombard"] },
  { code: "aligote", name: "Aligoté", colour: "white" },
  { code: "scheurebe", name: "Scheurebe", colour: "white" },
  { code: "kerner", name: "Kerner", colour: "white" },
  { code: "bacchus", name: "Bacchus", colour: "white" },
  { code: "grenache-blanc", name: "Grenache Blanc", colour: "white", aliases: ["Garnacha Blanca"] },
  { code: "picpoul", name: "Picpoul", colour: "white", aliases: ["Piquepoul", "Picpoul de Pinet"] },
  { code: "savagnin", name: "Savagnin", colour: "white", aliases: ["Heida"] },
  { code: "petit-manseng", name: "Petit Manseng", colour: "white" },
  { code: "gros-manseng", name: "Gros Manseng", colour: "white" },
  { code: "muscadelle", name: "Muscadelle", colour: "white" },
  { code: "chasselas", name: "Chasselas", colour: "white", aliases: ["Fendant", "Gutedel"] },
  { code: "rkatsiteli", name: "Rkatsiteli", colour: "white" },
  { code: "airen", name: "Airén", colour: "white" },
  { code: "grenache-gris", name: "Grenache Gris", colour: "white", aliases: ["Garnacha Gris"] },
];

const BY_CODE: ReadonlyMap<string, Grape> = new Map(GRAPES.map((g) => [g.code, g]));

const BY_NAME: ReadonlyMap<string, string> = new Map(
  GRAPES.flatMap((g) => [g.name, ...(g.aliases ?? [])].map((name) => [foldName(name), g.code])),
);

export function grapeByCode(code: string): Grape | undefined {
  return BY_CODE.get(code);
}

export function isGrapeCode(code: string): boolean {
  return BY_CODE.has(code);
}

/** The code for a grape as someone typed it (name, synonym or spelling variant), if we know it. */
export function grapeCodeOf(text: string): string | undefined {
  return BY_NAME.get(foldName(text));
}
